import { repairFunnelEmail } from '@/components/signup/funnel-email';
import SignupPage from '@/pages/SignupPage';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

const signupSearchSchema = z.object({
  redirect: z.string().optional(),
  // Attribution code from a QR/marketing link (?ref=). Recorded at signup.
  ref: z.string().optional(),
  // A locked plan to sell at signup time (an Alfie plan code or K&C tier code,
  // case-insensitive). Missing/invalid → the full plan-selector variant.
  plan: z.string().optional(),
  // Facebook click id from an ad link (?fbclid=). Recorded at signup into
  // user_signup_params; the GHL sale sync writes it to the CRM contact.
  fbclid: z.string().optional(),
  // Google Ads click ids, same treatment as fbclid. `gclid` is the standard
  // one; `wbraid`/`gbraid` replace it on iOS where Google can't set a gclid —
  // capturing only gclid silently loses those conversions.
  gclid: z.string().optional(),
  wbraid: z.string().optional(),
  gbraid: z.string().optional(),
  // Buyer email carried from the GHL landing page so the form arrives
  // pre-filled. User-editable here and in the URL, so it is never identity for
  // auth purposes — but it IS recorded (as funnel_email) into user_signup_params
  // and stamped on user_profiles.referral_signup_email, because an OAuth signup
  // arrives with the provider's email and the GHL sale sync needs the funnel's
  // address to find the contact that carries the ad click id.
  //
  // GHL pastes the address into the redirect URL unencoded, so a `+` in it
  // reaches the parser as a space — repairFunnelEmail maps interior spaces back
  // (an email can never contain one). Repaired HERE, at the parse boundary, so
  // every consumer (form prefill, funnel_email recording) sees the true address.
  email: z.string().optional().transform(repairFunnelEmail),
  // Name carried from the GHL landing page. PREFILL ONLY — it populates the
  // signup form's name field and nothing else; whatever the person submits is
  // what becomes their profile name. Not recorded server-side.
  //
  // This entry is load-bearing: validateSearch runs a zod object and zod STRIPS
  // keys it doesn't declare, so without it `?full_name=` is discarded before the
  // page ever sees it and the prefill silently does nothing.
  full_name: z.string().optional(),
  // ClickUp id of a course an admin has switched on as "free via link". Recorded
  // into user_signup_params (new accounts only, server-side); the course is then
  // visible to this person alone and they land on it after signing in.
  course: z.string().optional(),
});

export const Route = createFileRoute('/signup')({
  component: SignupPage,
  validateSearch: signupSearchSchema,
});
